// src/pages/Signup.js
import React, { useState, useContext } from "react";
import {
  UserOutlined,
  LockOutlined,
  DollarOutlined,
  ShopOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Typography,
  Alert,
  Upload,
  message,
} from "antd";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";

const { Title, Text } = Typography;

const API_URL = "https://zedlenders.pickmesms.com";
const SIGNUP_TOKEN = "W7x5N6NdlsMFk7VKFVvDpIx6rlueC1wB";
const MAX_CERT_SIZE_MB = 5;

const Signup = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [messageContent, setMessageContent] = useState({
    type: "",
    text: "",
  });

  // Uploads the PACRA certificate to Directus and returns its file id.
  const uploadCertificate = async (file) => {
    const formData = new FormData();
    formData.append("title", `PACRA Certificate - ${file.name}`);
    formData.append("file", file, file.name);

    const response = await axios.post(`${API_URL}/files`, formData, {
      headers: { Authorization: `Bearer ${SIGNUP_TOKEN}` },
    });

    return response.data.data.id;
  };

  const onFinish = async (values) => {
    setLoading(true);
    setShowMessage(false);
    try {
      // 1. Upload PACRA certificate first so the company record can reference it
      const certificateFile = values.pacraCertificate?.[0]?.originFileObj;
      const certificateId = await uploadCertificate(certificateFile);

      // 2. Create Company
      const companyResponse = await axios.post(
        `${API_URL}/items/companies`,
        {
          company_name: values.companyName,
          status: "published",
          balance: values.initialBalance,
          company_address: values.companyAddress,
          company_phone: values.companyPhone,
          pacra_reg_number: values.pacraRegNumber,
          pacra_certificate: certificateId,
          signup_date: new Date().toISOString(),
          plan: 1,
          plan_startdate: new Date().toISOString(),
          plan_enddate: new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          menus: [1, 2, 3, 4, 5, 6, 7, 8, 19, 27, 28],
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer W7x5N6NdlsMFk7VKFVvDpIx6rlueC1wB`,
          },
        },
      );

      //console.log(companyResponse);

      const userData = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        password: values.password,
        email_notifications: true,
        status: "draft",
        role: "186a1cb5-7b41-4b0e-b9eb-187cb5de75b1",
        company: companyResponse.data.data.id,
      };

      await axios.post(`${API_URL}/users`, userData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      setLoading(false);
      setMessageContent({
        type: "success",
        text: ` Activation link sent to ${values.email}. Check your inbox and verify within 24 hours. 🎉`,
      });
      setShowMessage(true);

      form.resetFields();

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setMessageContent({
        type: "error",
        text:
          error?.response?.data?.errors?.[0]?.message ||
          "We couldn't complete your registration. Please check your details and try again.",
      });
      setShowMessage(true);
    }
  };

  return (
    <Row
      justify="center"
      align="middle"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "40px 15px",
      }}
    >
      <Col xs={24} sm={22} md={20} lg={18} xl={14}>
        <Card
          hoverable
          style={{
            borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: "40px 32px" }}
        >
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <Title level={2} style={{ marginBottom: 8, color: "#2D3748" }}>
              Create Your Account
            </Title>
            <Text type="secondary" style={{ fontSize: 14 }}>
              Get started with our financial platform
            </Text>
          </div>

          <Form
            form={form}
            name="signupForm"
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Row gutter={24}>
              <Col span={24}>
                <Title level={4} style={{ marginBottom: 24 }}>
                  Company Information
                </Title>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Company Name"
                  name="companyName"
                  rules={[
                    { required: true, message: "Please input company name!" },
                  ]}
                >
                  <Input
                    prefix={<ShopOutlined />}
                    placeholder="Acme Corp"
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Initial Balance"
                  name="initialBalance"
                  rules={[
                    {
                      required: true,
                      message: "Please input initial balance!",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="0.00"
                    size="large"
                    min={0}
                    step={1000}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Company Address"
                  name="companyAddress"
                  rules={[
                    {
                      required: true,
                      message: "Please input company address!",
                    },
                  ]}
                >
                  <Input
                    prefix={<EnvironmentOutlined />}
                    placeholder="123 Business St."
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Company Phone"
                  name="companyPhone"
                  rules={[
                    { required: true, message: "Please input company phone!" },
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="+1 234 567 890"
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="PACRA Registration Number"
                  name="pacraRegNumber"
                  rules={[
                    {
                      required: true,
                      message: "Please input your PACRA registration number!",
                    },
                  ]}
                >
                  <Input
                    prefix={<IdcardOutlined />}
                    placeholder="e.g. 120200012345"
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="PACRA Company Registration Certificate"
                  name="pacraCertificate"
                  valuePropName="fileList"
                  getValueFromEvent={(e) =>
                    Array.isArray(e) ? e : e?.fileList
                  }
                  extra={`PDF or image, max ${MAX_CERT_SIZE_MB}MB`}
                  rules={[
                    {
                      required: true,
                      message:
                        "Please upload your PACRA registration certificate!",
                    },
                  ]}
                >
                  <Upload
                    maxCount={1}
                    accept=".pdf,.png,.jpg,.jpeg"
                    listType="text"
                    beforeUpload={(file) => {
                      const isAllowedType = [
                        "application/pdf",
                        "image/png",
                        "image/jpeg",
                      ].includes(file.type);
                      if (!isAllowedType) {
                        message.error(
                          "Certificate must be a PDF, PNG or JPG file!",
                        );
                        return Upload.LIST_IGNORE;
                      }
                      const isWithinSize =
                        file.size / 1024 / 1024 < MAX_CERT_SIZE_MB;
                      if (!isWithinSize) {
                        message.error(
                          `Certificate must be smaller than ${MAX_CERT_SIZE_MB}MB!`,
                        );
                        return Upload.LIST_IGNORE;
                      }
                      // Prevent antd from auto-uploading; we upload on submit.
                      return false;
                    }}
                  >
                    <Button icon={<InboxOutlined />} size="large" block>
                      Select Certificate
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Title level={4} style={{ marginBottom: 24, marginTop: 16 }}>
                  Admin Details
                </Title>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[
                    { required: true, message: "Please input first name!" },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="John"
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[
                    { required: true, message: "Please input last name!" },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Doe"
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    {
                      type: "email",
                      message: "Please input valid email!",
                    },
                    { required: true, message: "Please input email!" },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="admin@company.com"
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                    { required: true, message: "Please input password!" },
                    { min: 8, message: "Minimum 8 characters required!" },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="••••••••"
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Confirm Password"
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "Please confirm password!" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match!"),
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="••••••••"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
            {showMessage && (
              <div style={{ marginBottom: 24 }}>
                <Alert
                  message={messageContent.text}
                  type={messageContent.type}
                  showIcon
                  closable
                  onClose={() => setShowMessage(false)}
                  style={{
                    borderRadius: 8,
                    border: "none",
                    background:
                      messageContent.type === "success" ? "#f6ffed" : "#fff1f0",
                  }}
                />
              </div>
            )}
            <Form.Item style={{ marginTop: 32 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
                style={{
                  height: 48,
                  borderRadius: 8,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  fontWeight: 600,
                }}
              >
                Create Account
              </Button>
            </Form.Item>

            <div style={{ textAlign: "center", marginTop: 24 }}>
              <Text type="secondary">
                Already have an account?{" "}
                <a href="/login" style={{ color: "#667eea", fontWeight: 500 }}>
                  Sign In
                </a>
              </Text>
            </div>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default Signup;
